import React, { lazy, Suspense } from 'react'
import { Tab, Tabs, Button, Row, Col } from 'react-bootstrap'
import { IoIosAdd } from 'react-icons/io'
import { observable, action } from 'mobx'
import { observer } from 'mobx-react'
import isFunction from 'lodash/isFunction'
import isArray from 'lodash/isArray'
import {
  isToEnableItemLevelShipping,
  IS_BROWSER,
  APPConfig,
  convertToBoolean,
} from 'config/appConfig'
import {
  hasUserAccountAddress,
  isToShowLegalAddressOptionInAddressForm,
  stringifyAddress,
  isGuestLegalAgreementPending,
} from 'src/deps'
import {
  i18nTranslate,
  getContentFromCS,
  isB2BUser,
  isB2B2C,
  isExpressCheckout,
  scrollState,
  setLocalStorage,
  validateMaxLengthForAddressLine,
} from 'src/utils'
import { overlayState, toastState } from 'src/views/components'
import { MultipleShipping } from './MultipleShipping'
import { ShippingAddressList } from './ShippingAddressComponents'
import {
  cartContainer,
  checkoutContainer,
  customerContainer,
  nuskinSubscriptionContainer,
  storeContainer,
  nlxxConfigContainer,
} from 'src/models'
import { signInwithRedirect } from 'src/utils/signInUtils'
import { countriesWithoutPostalCodes } from 'src/static/geography'
import ExpressSectionHeader from '../ExpressSectionHeader'
import { CHECKOUT_PATHS } from 'src/views/pages/Checkout/fixture'
import './styles.scss'
import {
  initializeLegalAddressPreference as syncLegalAddressPreferenceFromStorage,
  handleLegalAddressRadioOption as persistLegalAddressPreference,
  normalizeLegalAddressPreference,
  applyDefaultAddressLegalPreference,
  isDefaultAddressCheckbox,
  isLegalAddressYesSelected,
  isLegalAddressNoSelected,
} from './legalAddressPreference'

@observer
class ShippingAddressListWithForm extends React.Component {
  @observable currentlySelectedAddress = ''
  @observable isToDisableContinueBtn = false
  @observable disableRadioBtn = false
  loadContentFromCS = true
  @observable showNewForm = false
  @observable showList = false
  @observable forceExpandForLegalAgreement = false
  signInType =
    APPConfig.getConfigFromJSON()?.defaultProps?.signInType || 'default'
  checkout = 'mysiteCheckout'
  enableEQFastFollow = convertToBoolean(
    APPConfig?.getAppConfig()?.enableEQFastFollow || false
  )
  loadContentFromCS = this.enableEQFastFollow || false
  AddressForm = ''
  currentMarket = storeContainer?.defaultShippingRegion
    ? 'Address' + storeContainer?.defaultShippingRegion
    : 'AddressUS'

  AddressForm = lazy(
    () =>
      import(
        'src/views/addresses/' + this.currentMarket + '/' + this.currentMarket
      )
  )

  // Memoized renderSubmitButton closures, keyed by legalRadioName - see getAddAddressContinueRenderer
  addAddressContinueRenderers = {}
  // Tracks whether the shopper has explicitly answered the legal-address radio (MDIGI-16273)
  isLegalAddressAnsweredByShopper = false

  constructor(props) {
    super(props)
    this.state = {
      contentData: {},
    }
    // Captured once at mount (not reactive) so checking the box doesn't
    // instantly re-collapse the form - only an actual submit does, via
    // handleAddressSubmit below.
    this.forceExpandForLegalAgreement = isGuestLegalAgreementPending()
    // Keep activePath in sync with the forced-open shipping form so
    // ExpressCheckout.handleNext() computes the correct next step from the
    // first Continue click instead of miscalculating from a stale activePath.
    if (this.forceExpandForLegalAgreement) {
      checkoutContainer.activePath = CHECKOUT_PATHS.SHIPPING
    }
    // Leave currentlySelectedAddress unset so the render falls through to the
    // editable AddressForm instead of the collapsed summary, until the guest
    // checks the Ironclad legal agreement checkbox.
    if (!this.forceExpandForLegalAgreement) {
      if (
        props.addressToSelectOrPrefill &&
        props.addressToSelectOrPrefill.addressLine1
      ) {
        this.currentlySelectedAddress = props.addressToSelectOrPrefill
      } else if (props.addressList && props.addressList.length) {
        const defaultAddress =
          props.addressList.find(accountAddress => accountAddress.isDefault) ||
          ''
        if (defaultAddress) {
          const isAddressIncomplete =
            defaultAddress.firstName === null ||
            defaultAddress.firstName === '' ||
            defaultAddress.phone === null ||
            defaultAddress.phone === ''
          if (!isAddressIncomplete) {
            this.currentlySelectedAddress = defaultAddress
          }
        }

        if (
          isExpressCheckout() &&
          customerContainer?.isRegisterUser &&
          !this.currentlySelectedAddress
        ) {
          this.showList = true
        }
      }
    }
    const defaultAddress = props.addressList.find(address => address.isDefault)
    if (defaultAddress) {
      // Put 'Default address' to index 0
      props.addressList.splice(
        props.addressList.findIndex(address => address.isDefault),
        1
      )
      props.addressList.splice(0, 0, defaultAddress)
    }
    let index = props.addressList.findIndex(obj => {
      return (
        obj.addressId ===
        Number(this.currentlySelectedAddress?.profileAddressId)
      )
    })
    if (index > 1) {
      props.addressList.splice(1, 0, props.addressList[index])
      if (index + 1 < props.addressList.length)
        props.addressList.splice(index + 1, 1)
    }
    this.initializeLegalAddressPreference()
  }

  async componentDidMount() {
    if (
      !nlxxConfigContainer.nlxxConfigStoreId ||
      nlxxConfigContainer.nlxxConfigStoreId !== storeContainer.activeStoreId
    ) {
      nlxxConfigContainer.loadNLXXConfig()
    }
    const isAddressIncomplete =
      cartContainer.cartResponse?.deliveryDetails?.address?.firstName ===
        null ||
      cartContainer.cartResponse?.deliveryDetails?.address?.firstName === '' ||
      cartContainer.cartResponse?.deliveryDetails?.address?.phone === null ||
      cartContainer.cartResponse?.deliveryDetails?.address?.phone === ''

    if (this.loadContentFromCS) {
      await this.getCSData()
    }
    const showAddressLineMaxLengthInfo = validateMaxLengthForAddressLine(
      this.props?.addressToSelectOrPrefill
    )
    if (showAddressLineMaxLengthInfo) {
      this.showList = true
    }
    if (
      isExpressCheckout() &&
      !customerContainer?.isRegisterUser &&
      Object.keys({ ...this.props?.addressToSelectOrPrefill })?.length > 0
    ) {
      const isLegalAgreementPending = isGuestLegalAgreementPending()
      if (isAddressIncomplete || isLegalAgreementPending) {
        this.showList = false
        if (isLegalAgreementPending) {
          checkoutContainer.activePath = CHECKOUT_PATHS.SHIPPING
        }
      } else {
        this.showList = true
      }
    }
    this.initializeLegalAddressPreference()
  }

  initializeLegalAddressPreference = () => {
    syncLegalAddressPreferenceFromStorage()
    /** Checking only the default-address checkbox must not count as answering the radio (see legalAddressPreference.js) */
    this.isLegalAddressAnsweredByShopper =
      checkoutContainer.isLegalAddressRadioAnsweredByShopper
  }

  handleLegalAddressRadioOption = (selectedValue = 'false') => {
    this.isLegalAddressAnsweredByShopper = true
    persistLegalAddressPreference(selectedValue)
  }

  // Defaults the legal-address answer to Yes when the shopper checks "Set as Default Address" (MDIGI-16273)
  handleShippingFormChange = event => {
    const target = event?.target
    if (
      !isDefaultAddressCheckbox(target) ||
      this.isLegalAddressAnsweredByShopper ||
      !isToShowLegalAddressOptionInAddressForm() ||
      hasUserAccountAddress()
    ) {
      return
    }
    applyDefaultAddressLegalPreference(target.checked)
  }

  componentDidUpdate(prevProps) {
    if (this.props?.shippingMethodAdded !== prevProps?.shippingMethodAdded) {
      if (
        Object.keys({ ...this.props?.addressToSelectOrPrefill })?.length > 0 &&
        this.props?.shippingMethodAdded &&
        isExpressCheckout()
      ) {
        this.toggleShowList()
        scrollState.smoothScrollToModalElement(
          `#qa-checkout-shipping-accordion`
        )
        if (customerContainer?.isRegisterUser) {
          this.showList = false
          this.showNewForm = false
          this.initializeData(this.props)
        }
      }
    }
    if (
      this.props.addressToSelectOrPrefill !== prevProps.addressToSelectOrPrefill
    ) {
      this.initializeData(this.props)
    }
    if (checkoutContainer?.enableShowAddressList) {
      this.showList = true
    }
  }

  formatAddress = address => {
    if (!address) return null
    const { addressLine1, addressLine2, city, county, state, zip, country } =
      address
    return (
      <span
        className="d-flex flex-row express-address-payment mt-2 flex-wrap"
        data-testid="qa-express-shipping-address">
        <span
          className="express-shiping-address-data"
          data-testid="qa-address-line-1">
          {addressLine1 || ''},
        </span>
        <span
          className="express-shiping-address-data"
          data-testid="qa-address-line-2">
          {addressLine2 && `${addressLine2}, `}
          {city && <span data-testid="qa-city">{city}, </span>}
          {county && <span data-testid="qa-county">{county}, </span>}
          {state && <span data-testid="qa-state">{state}, </span>}
          {zip && (
            <span className="express-shiping-address-data" data-testid="qa-zip">
              {zip},{' '}
            </span>
          )}
        </span>
        <span className="express-shiping-address-data" data-testid="qa-country">
          {country || ''}
        </span>
      </span>
    )
  }

  handleOverlayClose() {
    const interval = setInterval(() => {
      if (
        IS_BROWSER &&
        !!window?.performance &&
        window?.performance?.navigation?.type === 2
      ) {
        clearInterval(interval)
        overlayState?.hideLoader()
      }
    }, 500)
  }

  componentWillUnmount() {
    if (isExpressCheckout()) {
      overlayState?.hideLoader()
    }
  }

  initializeData = props => {
    // Keep currentlySelectedAddress unset so the editable AddressForm stays
    // visible instead of reverting to the collapsed summary.
    if (!isGuestLegalAgreementPending()) {
      if (
        props?.addressToSelectOrPrefill &&
        props?.addressToSelectOrPrefill?.addressLine1
      ) {
        this.currentlySelectedAddress = props?.addressToSelectOrPrefill
      } else if (props?.addressList && props?.addressList.length) {
        this.currentlySelectedAddress = props?.addressList.find(
          accountAddress => accountAddress?.isDefault
        )
      }
    }
    const defaultAddress = props?.addressList?.find(
      address => address?.isDefault
    )
    if (defaultAddress) {
      // Put 'Default address' to index 0
      props?.addressList?.splice(
        props?.addressList?.findIndex(address => address.isDefault),
        1
      )
      props?.addressList?.splice(0, 0, defaultAddress)
    }
    let index = props?.addressList?.findIndex(obj => {
      return (
        obj?.addressId ===
        Number(this.currentlySelectedAddress?.profileAddressId)
      )
    })
    if (index > 1) {
      props?.addressList?.splice(1, 0, props?.addressList?.[index])
      if (index + 1 < props?.addressList?.length)
        props?.addressList?.splice(index + 1, 1)
    }
  }

  getCSData = async () => {
    const contentData = await getContentFromCS('jitsu_strings')
    let promise = new Promise((resolve, reject) => {
      if (Object.keys(contentData).length > 0) {
        resolve(contentData)
      } else {
        reject(Error('Promise rejected'))
      }
    })

    promise.then(
      result => {
        this.setState({ contentData: result })
      },
      function (error) {
        this.setState({ contentData: error })
      }
    )
  }
  @action
  toggleShowList = () => {
    const checkValidAddress = validateMaxLengthForAddressLine(
      this.props?.addressToSelectOrPrefill
    )
    this.showList = checkValidAddress ? true : !this.showList
    this.initializeData(this.props)
    if (!this.showList) {
      this.showNewForm = false
      checkoutContainer.enableShowAddressList = false
      checkoutContainer.disableSubmitButtonAsItemsNotDeliverable = false
      checkoutContainer.isItemsNotDeliverableErrorIN = false
      checkoutContainer.isShippingAddNewFormOpen = false
    }
  }
  @action
  toggleShowListForm = () => {
    this.initializeLegalAddressPreference()
    checkoutContainer.isItemsNotDeliverableErrorIN = false
    this.showNewForm = !this.showNewForm
    checkoutContainer.isShippingAddNewFormOpen = this.showNewForm
  }

  handleSelect = accountAddress => {
    this.currentlySelectedAddress = accountAddress
    this.showNewForm = false
    checkoutContainer.isShippingAddNewFormOpen = false
    isFunction(this.props.handleAddressSelect) &&
      this.props.handleAddressSelect(accountAddress)
    if (isExpressCheckout()) {
      this.props?.handleAddressContinue(this.currentlySelectedAddress)
    }
  }

  newFormSelectionChecker = () => {
    // this flow will be executed only if addressList.length > 0
    let selectedAddressIndex = -1
    if (this.props.addressToSelectOrPrefill === '') {
      // if there is no address is in cart
      // but has addressList
      // select addresslist tab
      return false
    } else {
      // has address in cart
      // check whether cart address(addressToSelectOrPrefill) is in `addressList`
      selectedAddressIndex = this.props.addressList.findIndex(
        accountAddress =>
          stringifyAddress(accountAddress) ===
          stringifyAddress(this.props.addressToSelectOrPrefill)
      )
      // if selectedIndex === -1
      // then address is not available in addressList of myaccount
      // so choose new address form
      return selectedAddressIndex === -1
    }
  }

  renderContinueButton = () => {
    const { handleAddressContinue, addressToSelectOrPrefill } = this.props
    return (
      <Button
        disabled={
          this.props.isToDisableShippingContinueBtn ||
          this.isToDisableContinueBtn ||
          false
        }
        className="rounded-0 shipping-continue px-5 py-2 shipping-button btn-bg-blue"
        data-testid="qa-ship-continue"
        onClick={() =>
          handleAddressContinue(
            this.currentlySelectedAddress || addressToSelectOrPrefill
          )
        }>
        {i18nTranslate('checkout.continue', 'Continue')}
      </Button>
    )
  }

  renderSaveChangesButton = () => {
    const { handleAddressContinue, addressToSelectOrPrefill } = this.props
    return (
      <Button
        disabled={this.props.isToDisableShippingContinueBtn || false}
        className="shipping-save-changes px-5 py-2 shipping-button"
        data-testid="qa-ship-continue"
        onClick={() =>
          handleAddressContinue(
            this.currentlySelectedAddress || addressToSelectOrPrefill
          )
        }>
        {i18nTranslate('checkout.saveChanges', 'Save Changes')}
      </Button>
    )
  }

  handleAddressSubmit = async formData => {
    const formData1 = {
      ...formData,
      // country: storeContainer.storeIDValue,
    }
    const { handleModal, addressList = [] } = this.props
    const { state = '', country, stateList, ...remainingFormData } = formData1
    const stateValue = this.isStateFieldDropDown === true ? stateList : state
    const transformedFormData = {
      state: stateValue || '',
      country: country?.value ? country?.value : country,
      ...(countriesWithoutPostalCodes && {
        zip: formData1.postalCode || '000',
      }),
      ...remainingFormData,
      ...(checkoutContainer.isLegalAddressPreferenceSet && {
        legalAddressSameAsShippingAddress: normalizeLegalAddressPreference(
          checkoutContainer.isLegalAddressSameAsShipping
        ),
      }),
    }
    const newAddressData = {
      ...transformedFormData,
      default: true,
      street1: transformedFormData.addressLine1,
      street2: transformedFormData.addressLine2,
      zipCode: transformedFormData.zip || transformedFormData.postalCode,
    }

    if (!isArray(addressList) || addressList.length < 1000) {
      // Only release the forced-open state once the guest has actually
      // agreed - if still unchecked, ShippingAccordion blocks the step
      // advance anyway, so keep the form visible alongside the checkbox.
      if (!isGuestLegalAgreementPending()) {
        this.forceExpandForLegalAgreement = false
      }
      handleModal(transformedFormData, newAddressData)
    } else {
      toastState.setToastMessage(
        i18nTranslate(
          'checkoutAddress.limitExceeded',
          'Maximum number of addresses that can be setup is already reached'
        ),
        false
      )
    }
  }

  renderLegalAddressSameAsShippingOptions = (
    radioName = 'legalAddressAddNew'
  ) => {
    const isToShowLegalAddressChangesInCheckout =
      isToShowLegalAddressOptionInAddressForm() || false

    if (isToShowLegalAddressChangesInCheckout && !hasUserAccountAddress()) {
      const yesId = `${radioName}Yes`
      const noId = `${radioName}No`
      return (
        <fieldset
          className="legal-address-options-wrapper"
          data-testid="qa-legal-address-same-as-shipping-new-address">
          <legend className="legal-address-text-wrapper">
            <span className="legal-same-as-shipping d-block mb-2">
              {i18nTranslate(
                'legalAddress.legalAddressSameAsShippingAddress',
                'Is your legal address the same as your shipping address?'
              )}
            </span>
            <span className="legal-address-confirmation-text d-block mb-2">
              {i18nTranslate(
                'legalAddress.addressConfirmationText',
                'This is your permanent address that is used for account and tax purposes.'
              )}
            </span>
          </legend>
          <div className="legal-address-radio-options d-flex">
            <input
              type="radio"
              name={radioName}
              id={yesId}
              data-testid="qa-address-legal-address-same-new-address"
              onChange={() => {
                this.handleLegalAddressRadioOption('true')
              }}
              checked={isLegalAddressYesSelected()}
            />
            <label htmlFor={yesId} className="col-2 col-md-2">
              {i18nTranslate('legalAddress.yesOption', 'Yes')}
            </label>
            <input
              type="radio"
              name={radioName}
              id={noId}
              data-testid="qa-address-legal-address-not-same-new-address"
              onChange={() => {
                this.handleLegalAddressRadioOption('false')
              }}
              checked={isLegalAddressNoSelected()}
            />
            <label htmlFor={noId} className="col-2 col-md-2">
              {i18nTranslate('legalAddress.noOption', 'No')}
            </label>
          </div>
        </fieldset>
      )
    }
    return <></>
  }
  renderTabs = props => {
    const { index, AddressForm } = props
    const savedAddress = i18nTranslate('checkout.savedAddress', 'Saved Address')
    const expressSavedAddress = i18nTranslate(
      'expressCheckout.addShippingAddress',
      'Ship To'
    )
    const newAddress = i18nTranslate(
      'checkout.addNewAddress',
      'Add New Address'
    )
    const shouldSelectNewAddressFormTab = this.newFormSelectionChecker()
    const {
      isFromEditSubscription = false,
      addressToSelectOrPrefill = {},
      addressList = [],
      isFromCheckout = false,
    } = this.props
    const shippingButton = isFromEditSubscription
      ? this.renderSaveChangesButton()
      : this.renderContinueButton()

    if (isExpressCheckout()) {
      // Force the editable form open (instead of the collapsed summary or
      // the saved-address picker) while the guest hasn't checked the legal
      // agreement checkbox.
      const forceExpandForLegalAgreement = this.forceExpandForLegalAgreement
      return (
        <>
          <ExpressSectionHeader
            title={expressSavedAddress}
            dataTestId="qa-express-shipping-address-header"
            isOpen={this.showList && !forceExpandForLegalAgreement}
            onClick={() => this.toggleShowList()}
          />

          {!this.showList &&
            this.currentlySelectedAddress &&
            !forceExpandForLegalAgreement && (
              <div
                className="d-flex flex-column express-header-body"
                data-testid="qa-express-shipping-address-body">
                <span
                  className="fname-normal-shiping-express"
                  data-testid="qa-express-shipping-address-name">
                  {`${this.currentlySelectedAddress?.firstName} ${
                    this.currentlySelectedAddress?.firstName?.toUpperCase() !==
                    this.currentlySelectedAddress?.lastName?.toUpperCase()
                      ? this.currentlySelectedAddress?.lastName
                      : ''
                  }`}
                </span>
                {this.formatAddress(this.currentlySelectedAddress)}
              </div>
            )}
          {((!this.showList && !this.currentlySelectedAddress) ||
            forceExpandForLegalAgreement) && (
            <Suspense fallback="">
              <div>
                <AddressForm
                  shippingAddresses={addressList}
                  onSubmit={this.handleAddressSubmit}
                  renderSubmitButton={this.getAddAddressContinueRenderer(
                    'legalAddressExpressInitialAddNew'
                  )}
                  embeddedSubmit={true}
                  type="checkout"
                  isAddressNotAdded={this.props?.isAddressNotAdded}
                  isToHideLegalAddressRadioOptions={true}
                />
              </div>
            </Suspense>
          )}
          {this.showList && !forceExpandForLegalAgreement && (
            <ShippingAddressList
              handleContinueBtnState={this.handleContinueBtnState}
              currentlySelectedAddress={this.currentlySelectedAddress}
              isAddressChecked={this.props.isAddressChecked}
              addressList={addressList}
              index={index}
              handleSelect={this.handleSelect}
              disableRadioBtn={this.props.isToDisableShippingContinueBtn}
              validationCallback={this.props.validationCallback}
              isAddressNotAdded={this.props?.isAddressNotAdded}
              setIsAddressNotAdded={this.props?.setIsAddressNotAdded}
              updateAddressAfterCartSuccess={cb =>
                this.props?.updateAddressAfterCartSuccess(cb)
              }
              hideLegalAddressOptions={this.showNewForm}
              legalAddressRadioName="legalAddressExpressSaved"
            />
          )}
          {(!isB2BUser() || isB2B2C()) && IS_BROWSER && (
            <>
              {this.showList && !forceExpandForLegalAgreement && (
                <h6
                  onClick={() => this.toggleShowListForm()}
                  className={`mb-2 mt-2 d-flex use-different-btn align-items-center ${nuskinSubscriptionContainer?.isToShowBlackOut == true ? 'black-out-view-disabled' : ''} `}>
                  <>
                    <IoIosAdd />{' '}
                    <span>
                      {i18nTranslate(
                        'expressCheckout.expressUseDifferentAddress',
                        'Use a different address'
                      )}
                    </span>
                  </>
                </h6>
              )}
              {this.showNewForm && (
                <Suspense fallback="">
                  <div>
                    <AddressForm
                      shippingAddresses={addressList}
                      onSubmit={this.handleAddressSubmit}
                      renderSubmitButton={this.getAddAddressContinueRenderer(
                        'legalAddressExpressAddNew'
                      )}
                      embeddedSubmit={true}
                      type="checkout"
                      isAddressNotAdded={this.props?.isAddressNotAdded}
                      isToHideLegalAddressRadioOptions={true}
                    />
                  </div>
                </Suspense>
              )}
            </>
          )}
        </>
      )
    }
    return (
      <Tabs
        className="my-3"
        defaultActiveKey={
          shouldSelectNewAddressFormTab ? 'newAddress' : 'savedAddress'
        }
        id="">
        <Tab
          eventKey="savedAddress"
          title={savedAddress}
          data-testid="qa-address-container">
          <ShippingAddressList
            handleContinueBtnState={this.handleContinueBtnState}
            currentlySelectedAddress={this.currentlySelectedAddress}
            isAddressChecked={this.props.isAddressChecked}
            addressList={addressList}
            index={index}
            handleSelect={this.handleSelect}
            disableRadioBtn={this.props.isToDisableShippingContinueBtn}
            validationCallback={this.props.validationCallback}
            updateAddressAfterCartSuccess={cb =>
              this.props?.updateAddressAfterCartSuccess(cb)
            }
          />
          <Row className="justify-content-end pt-3" noGutters>
            {shippingButton}
          </Row>
        </Tab>
        {(!isB2BUser() || isB2B2C()) && IS_BROWSER && (
          <Tab eventKey="newAddress" title={newAddress}>
            <Suspense fallback="">
              {isExpressCheckout() && (
                <ExpressSectionHeader
                  title={expressSavedAddress}
                  disableAccordion={true}
                />
              )}

              <AddressForm
                //Commented below code for CX16-9938 for StoppingAutofill in AddNewAddress
                // addressInfo={
                //   shouldSelectNewAddressFormTab
                //     ? addressToSelectOrPrefill || ''
                //     : ''
                // }
                shippingAddresses={addressList}
                onSubmit={this.handleAddressSubmit}
                renderSubmitButton={this.getAddAddressContinueRenderer(
                  'legalAddressNormalAddNew'
                )}
                embeddedSubmit={true}
                type={'checkout'}
                isToHideLegalAddressRadioOptions={true}
              />
            </Suspense>
          </Tab>
        )}
      </Tabs>
    )
  }

  // renderShippingAddressComponent = (addressInfo, index) => {
  //   return (
  //     <AddressList2
  //       addressInfo={addressList}
  //       index={index}
  //       key={`addressInfo${addressInfo.addressId}`}
  //     />
  //   )
  // }

  handleContinueBtnState = value => {
    this.isToDisableContinueBtn = value
  }

  renderChangeMultipleAddressOption = () => {
    if (this.props.isMultipleAdressActive) {
      return (
        <div
          className="shipping-address-link text-right"
          onClick={this.props.toggleMultipleAddress}>
          {i18nTranslate('checkout.shipToSingle', 'Ship to single addresses')}
        </div>
      )
    } else {
      return (
        <div
          className="shipping-address-link text-right d-none"
          onClick={this.props.toggleMultipleAddress}>
          {i18nTranslate(
            'checkout.shipToMultiple',
            'Ship to multiple addresses'
          )}
        </div>
      )
    }
  }

  renderAddAddressContinueButtonRow = (onContinue = null) => {
    return (
      <Row noGutters className="justify-content-end">
        <Button
          className="continue-button mt-3 px-5 py-2 btn-bg-blue add-continue-button"
          disabled={
            this.props.isToDisableShippingContinueBtn ||
            checkoutContainer.isInvalidPhoneCountryCodeIN ||
            false
          }
          // Smarty markets submit through the package's own hidden button.
          type={onContinue ? 'button' : 'submit'}
          onClick={onContinue || undefined}
          data-testid="qa-ship-continue">
          {i18nTranslate('checkoutAddress.submit', 'Continue')}
        </Button>
      </Row>
    )
  }

  // Kept for existing test coverage asserting on the plain <Row> shape.
  renderAddAddressContinueButton = () => this.renderAddAddressContinueButtonRow()

  /**
   * The address form mounts the submit renderer as a COMPONENT inside a
   * non-observer form, so the MobX reads for the embedded radio must be
   * tracked here, by an observer of its own (MDIGI-16283).
   *
   * `onContinue`/`showContinue` are supplied by the
   * @nuskin/shipping-address-form adapter, which owns its own submit button.
   */
  AddAddressContinueButton = observer(
    ({ legalRadioName = '', onContinue = null, showContinue = true }) => {
      return (
        <>
          {legalRadioName &&
            this.renderLegalAddressSameAsShippingOptions(legalRadioName)}
          {showContinue && this.renderAddAddressContinueButtonRow(onContinue)}
        </>
      )
    }
  )

  // One renderer per radio group, memoised so the form (which mounts it as a
  // component) doesn't remount the button - and lose focus - on every render.
  getAddAddressContinueRenderer = legalRadioName => {
    if (!this.addAddressContinueRenderers[legalRadioName]) {
      const ContinueButton = this.AddAddressContinueButton
      this.addAddressContinueRenderers[legalRadioName] = (props = {}) => (
        <ContinueButton {...props} legalRadioName={legalRadioName} />
      )
    }
    return this.addAddressContinueRenderers[legalRadioName]
  }

  renderShippingAddress = AddressForm => {
    const {
      isFromSubscription = false,
      addressList = [],
      isFromCheckout = false,
      addressToSelectOrPrefill = {},
    } = this.props

    const addressFormRender = () => {
      return (
        <AddressForm
          addressInfo={
            this.props?.addressList?.length ? addressToSelectOrPrefill : {}
          }
          onSubmit={this.handleAddressSubmit}
          shippingAddresses={addressList}
          type="checkout"
          renderSubmitButton={this.getAddAddressContinueRenderer(
            'legalAddressNoSavedAddNew'
          )}
          embeddedSubmit={true}
          loadContentFromCS={this.loadContentFromCS}
          enableEQFastFollow={this.enableEQFastFollow}
          isToHideLegalAddressRadioOptions={true}
        />
      )
    }
    if (
      this.props.isMultipleAdressActive &&
      isToEnableItemLevelShipping &&
      !isFromSubscription
    ) {
      return <MultipleShipping {...this.props} />
    } else {
      if (this.props.addressList && this.props.addressList.length) {
        return <>{this.renderTabs({ AddressForm: this.AddressForm })}</>
      } else {
        if (IS_BROWSER) {
          if (isExpressCheckout()) {
            let prefillAddress = Object.keys({
              ...addressToSelectOrPrefill,
            })?.length
            const currentlySelectedAddress = { ...addressToSelectOrPrefill }
            const isAddressIncomplete =
              currentlySelectedAddress?.firstName === null ||
              currentlySelectedAddress?.firstName === ''
            if (isAddressIncomplete) {
              prefillAddress = 0
            }
            // Force the editable form open (instead of the collapsed
            // summary) while the guest hasn't checked the legal agreement.
            const forceExpandForLegalAgreement =
              this.forceExpandForLegalAgreement
            return (
              <>
                <ExpressSectionHeader
                  title={i18nTranslate('expresscart.shiptotitle', 'Ship to')}
                  dataTestId="qa-express-shipping-address-header"
                  disableAccordion={prefillAddress === 0}
                  isOpen={!this.showList || forceExpandForLegalAgreement}
                  onClick={
                    prefillAddress > 0 ? () => this.toggleShowList() : () => {}
                  }
                />
                {this.signInType !== 'default' &&
                  !customerContainer.isRegisterUser && (
                    <div className="align-items-center d-flex">
                      <h6 className="mb-0" id="express-signin-heading">
                        {i18nTranslate(
                          'signin.expressSignInMessage',
                          'Already have an account?'
                        )}
                      </h6>
                      <button
                        className="okta-button express_signin_option"
                        data-testid="qa-okta-login"
                        aria-labelledby="express-signin-heading express-signin-label"
                        onClick={() => {
                          if (this.props?.isFromPDP) {
                            setLocalStorage('isExpressPDPLogin', true)
                          }
                          overlayState?.showLoader()
                          this.handleOverlayClose()
                          signInwithRedirect(this.checkout)
                        }}>
                        <span id="express-signin-label">
                          {i18nTranslate('signin.okta', 'Sign In')}
                        </span>
                      </button>
                    </div>
                  )}
                {this.showList && !forceExpandForLegalAgreement && (
                  <div
                    className="d-flex flex-column express-header-body"
                    data-testid="qa-express-shipping-address-body">
                    <span
                      className="fname-normal-shiping-express"
                      data-testid="qa-express-shipping-address-name">
                      {`${currentlySelectedAddress?.firstName} ${
                        currentlySelectedAddress?.firstName?.toUpperCase() !==
                        currentlySelectedAddress?.lastName?.toUpperCase()
                          ? currentlySelectedAddress?.lastName
                          : ''
                      }`}
                    </span>
                    {this.formatAddress(this.currentlySelectedAddress)}
                  </div>
                )}
                {(!this.showList || forceExpandForLegalAgreement) && (
                  <Suspense fallback="">
                    <div>
                      <AddressForm
                        addressInfo={addressToSelectOrPrefill}
                        onSubmit={this.handleAddressSubmit}
                        isAddressNotAdded={
                          this.props?.isAddressNotAdded || false
                        }
                        shippingAddresses={addressList}
                        type="checkout"
                        renderSubmitButton={this.getAddAddressContinueRenderer(
                          'legalAddressExpressNoSaved'
                        )}
                        embeddedSubmit={true}
                        isToHideLegalAddressRadioOptions={true}
                      />
                    </div>
                  </Suspense>
                )}
              </>
            )
          }
          return (
            <Suspense fallback="">
              {this.loadContentFromCS
                ? Object.keys(this.state?.contentData)?.length > 0 &&
                  addressFormRender()
                : addressFormRender()}
            </Suspense>
          )
        }
      }
    }
  }

  render() {
    /**
     * if radio input changes,
     * preference will be for currentlySelectedAddress
     * so don't change the below order in addressToDisplay
     */
    if (this.props.addressList && this.props.addressList.length) {
      return (
        <div
          data-testid="qa-shipping-container"
          onChange={this.handleShippingFormChange}>
          <Col className="p-0">
            <div
              className={`d-flex ${
                this.props.isMultipleAdressActive
                  ? 'justify-content-end'
                  : 'justify-content-start'
              } ${isExpressCheckout() ? '' : 'm-2 pb-3'}`}></div>
          </Col>
          {this.renderShippingAddress(this.AddressForm)}
        </div>
      )
    } else {
      return (
        <div
          data-testid="qa-shipping-container"
          onChange={this.handleShippingFormChange}>
          {/* {cartProductsLength > 1 &&
            isToEnableItemLevelShipping &&
            !isFromSubscription &&
            this.renderChangeMultipleAddressOption()} */}
          {this.renderShippingAddress(this.AddressForm)}
        </div>
      )
    }
  }
}

export { ShippingAddressListWithForm }
export default ShippingAddressListWithForm
